import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { trackEvent } from "@needl-ai/common/analytics";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@needl-ai/common/components/ui/dialog";
import { useGetPathPrefix } from "@needl-ai/common/workspace";

import EmptyState from "./searchHistory/EmptyState";
import HistorySection from "./searchHistory/HistorySection";
import SearchInput from "./searchHistory/SearchInput";
import SearchLoadingSkeleton from "./searchHistory/SearchLoadingSkeleton";
import { categorizeByTime } from "./searchHistory/utils";

import type { SearchHistoryItem } from "./searchHistory/types";

import { useFetchAllSession } from "@/hooks/useCreateUpdateSession";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";

const SearchHistoryModal = ({ isOpen, onClose }: SearchHistoryModalProps) => {
  const navigate = useNavigate();
  const pathPrefix = useGetPathPrefix();
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");

  // Debounce search input to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchInput(searchInput);
      if (searchInput.trim()) {
        trackEvent("search_chat", { query: searchInput.trim() });
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    data: sessions = [],
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFetchAllSession({
    q: debouncedSearchInput || undefined,
    askneedl_type: "SUPERCHARGING_ASKNEEDL",
  });

  const { containerRef, sentinelRef } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  // Transform sessions to SearchHistoryItem format
  const searchHistory: SearchHistoryItem[] = sessions.map((session) => ({
    id: session.id,
    query: session.title,
    date: new Date(session.updated_at),
    sessionId: session.id,
    matchedSentence: session.matched_sentence,
  }));

  const categorizedHistory = categorizeByTime(searchHistory);

  const handleItemClick = (item: SearchHistoryItem) => {
    trackEvent("search_chat_opened", {
      session_id: item.sessionId,
      query: item.query,
    });
    if (item.sessionId) {
      navigate(`${pathPrefix}/c/${item.sessionId}`);
    } else {
      // If no session ID, create a new session with this query
      navigate(`${pathPrefix}/`, { state: { initialPrompt: item.query } });
    }
    onClose();
  };

  // Reset search input when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchInput("");
      setDebouncedSearchInput("");
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="w-[90vw] max-w-[42.5rem] min-w-[20rem] h-[80vh] max-h-[80vh] p-0 flex flex-col fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border-gray-200 dark:bg-neutral-900 dark:border-neutral-800"
        overlayClassName="bg-black/20"
      >
        <DialogHeader className="flex-shrink-0 p-6 pb-4 border-b border-gray-200 dark:border-neutral-800">
          <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white">
            Search History
          </DialogTitle>

          <SearchInput value={searchInput} onChange={setSearchInput} />
        </DialogHeader>

        {/* Scrollable Content Area */}
        <div
          ref={containerRef}
          className="flex-1 overflow-y-auto p-6 min-h-0"
          style={{ scrollBehavior: "smooth" }}
        >
          {isLoading ? (
            <SearchLoadingSkeleton title="Loading search history..." />
          ) : error ? (
            <div className="flex items-center justify-center h-full min-h-[12.5rem]">
              <div className="text-sm text-red-600 dark:text-red-400">
                Error loading search results
              </div>
            </div>
          ) : searchHistory.length === 0 ? (
            <div className="flex items-center justify-center h-full min-h-[12.5rem]">
              <EmptyState hasSearchInput={!!searchInput} />
            </div>
          ) : (
            <div className="space-y-4">
              <HistorySection
                title="Today"
                items={categorizedHistory.today}
                onItemClick={handleItemClick}
                hasSearchQuery={!!debouncedSearchInput}
                searchQuery={debouncedSearchInput}
              />
              <HistorySection
                title="Last 7 Days"
                items={categorizedHistory.last7Days}
                onItemClick={handleItemClick}
                hasSearchQuery={!!debouncedSearchInput}
                searchQuery={debouncedSearchInput}
              />
              <HistorySection
                title="Last 30 Days"
                items={categorizedHistory.last30Days}
                onItemClick={handleItemClick}
                hasSearchQuery={!!debouncedSearchInput}
                searchQuery={debouncedSearchInput}
              />
              <HistorySection
                title="Older"
                items={categorizedHistory.older}
                onItemClick={handleItemClick}
                hasSearchQuery={!!debouncedSearchInput}
                searchQuery={debouncedSearchInput}
              />
              <div ref={sentinelRef} className="h-1" />
              {isFetchingNextPage && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-primary" />
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

type SearchHistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export default SearchHistoryModal;
